import React, { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Polyline, Tooltip, useMap } from "react-leaflet";
import { Car } from "lucide-react";

const lerp = (a, b, t) => a + (b - a) * t;

const Fit = ({ pts }) => {
  const map = useMap();
  useEffect(() => { if (pts.length > 1) map.fitBounds(pts, { padding: [40, 40] }); }, [pts, map]);
  return null;
};

// Simulated driver moving from `from` -> `to` with a live ETA countdown.
export const LiveRideTrack = ({ from, to, height = 300, durationSec = 40 }) => {
  const [progress, setProgress] = useState(0);
  const startRef = useRef(Date.now());

  useEffect(() => {
    startRef.current = Date.now();
    const id = setInterval(() => {
      const elapsed = (Date.now() - startRef.current) / 1000;
      setProgress(Math.min(1, elapsed / durationSec));
    }, 250);
    return () => clearInterval(id);
  }, [from, to, durationSec]);

  if (!from || !to) return null;
  const pos = [lerp(from[0], to[0], progress), lerp(from[1], to[1], progress)];
  const etaMin = Math.max(0, Math.round((1 - progress) * (durationSec / 60) * 18)); // scaled demo ETA
  const arrived = progress >= 1;

  return (
    <div className="relative">
      <div className="absolute z-[1000] top-3 left-3 glass-solid text-white text-xs px-3 py-2 rounded-lg flex items-center gap-2">
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#22D3EE] opacity-70" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#22D3EE]" />
        </span>
        {arrived ? "Driver has arrived 🎉" : `Driver en route · ETA ${etaMin} min`}
      </div>
      <div style={{ height }} className="w-full rounded-[var(--radius)] overflow-hidden border border-white/10">
        <MapContainer center={from} zoom={7} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
          <TileLayer attribution='&copy; OpenStreetMap &copy; CARTO' url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" />
          <Polyline positions={[from, to]} pathOptions={{ color: "#7C3AED", weight: 3, opacity: 0.5, dashArray: "6 8" }} />
          <Polyline positions={[from, pos]} pathOptions={{ color: "#22D3EE", weight: 4, opacity: 0.9 }} />
          <CircleMarker center={from} radius={6} pathOptions={{ color: "#7C3AED", fillColor: "#7C3AED", fillOpacity: 0.9 }} />
          <CircleMarker center={to} radius={7} pathOptions={{ color: "#F7C948", fillColor: "#F7C948", fillOpacity: 0.9 }}>
            <Tooltip>Pickup</Tooltip>
          </CircleMarker>
          <CircleMarker center={pos} radius={12} pathOptions={{ color: "#22D3EE", fillColor: "#22D3EE", fillOpacity: 0.25, weight: 1 }} />
          <CircleMarker center={pos} radius={7} pathOptions={{ color: "#6EE7FF", fillColor: "#22D3EE", fillOpacity: 1, weight: 2 }}>
            <Tooltip permanent direction="top">🚗 Driver</Tooltip>
          </CircleMarker>
          <Fit pts={[from, to]} />
        </MapContainer>
      </div>
    </div>
  );
};
